import { Controller } from "@hotwired/stimulus";
import { effect } from "@preact/signals-core";
import { uuid } from "../live/uuid";
import { clampMark, NOTE_RADIUS, NOTE_TAGS } from "../maps/notes";
import type { LiveNote } from "../types/generated/live";
import type MapBoard from "./map_board_controller";

const GAP = 12;
// A mark's halo and the row of tag chips beside it, which the card clears.
const HALO = 5;
const CHIP_STEP = 18;
const WRITE_MS = 400;
// Tailwind's max-sm: below it the card is a bottom sheet, placed by CSS.
const SHEET = "(max-width: 639px)";

type Field = "mark" | "text";

// Connects to data-controller="map-note-card", beside map-board on the master's own board root. The card follows the board's
// selected note: it opens beside the mark, edits the note through the board's optimistic writes and closes with the selection.
export default class extends Controller {
	static targets = ["panel", "mark", "text", "tag"];
	declare readonly panelTarget: HTMLElement;
	declare readonly markTarget: HTMLInputElement;
	declare readonly textTarget: HTMLTextAreaElement;
	declare readonly tagTargets: HTMLButtonElement[];
	id?: string | null;
	dispose?: () => void;
	timers: Partial<Record<Field, ReturnType<typeof setTimeout>>> = {};
	// One undo step for what is typed while a field keeps focus.
	sessions: Partial<Record<Field, string>> = {};
	refocusOnClick?: () => void;

	connect() {
		if (this.board?.renderer) this.ready();
	}

	disconnect() {
		this.dispose?.();
		this.dispose = undefined;
		this.close();
	}

	get board() {
		return this.application.getControllerForElementAndIdentifier(this.element, "map-board") as MapBoard | null;
	}

	ready() {
		const { state } = this.board;
		this.dispose?.();
		this.dispose = effect(() => {
			const id = state.local.note?.value;
			const note = id ? state.view.value.notes?.[id] : null;
			state.local.viewport.value;
			if (!note) return this.close();
			if (id !== this.id) this.open(note);
			this.fill(note);
			this.place(note);
		});
	}

	open(note: LiveNote) {
		this.close();
		this.id = note.id;
		this.panelTarget.hidden = false;
		// Whatever the previous note left in a focused field is not this note's.
		this.fill(note, true);
		// On a phone the card is a sheet over the tray panel, which steps aside for it.
		this.element.setAttribute("data-note-card", "open");
		this.board.renderer?.reveal({ x: note.x, y: note.y }, NOTE_RADIUS + GAP);
		// A new note is empty, so it takes the typing at once; a touch's mousedown comes after the tap that opened the card and
		// would focus the map, so the click that ends it hands the focus back.
		if (note.text) return;
		this.textTarget.focus({ preventScroll: true });
		this.refocusOnClick = () => this.id && !this.panelTarget.contains(document.activeElement) && this.textTarget.focus({ preventScroll: true });
		window.addEventListener("click", this.refocusOnClick, { once: true, capture: true });
	}

	close() {
		if (!this.id) return;
		this.flush();
		this.id = null;
		window.removeEventListener("click", this.refocusOnClick, { capture: true });
		this.panelTarget.hidden = true;
		this.element.removeAttribute("data-note-card");
		if (this.panelTarget.contains(document.activeElement)) this.board?.canvasTarget.focus({ preventScroll: true });
	}

	// The fields show the note, bar the one being typed in here; a change from another master's tab lands in the others.
	fill(note: LiveNote, force = false) {
		if (force || document.activeElement !== this.markTarget) this.markTarget.value = note.mark;
		if (force || document.activeElement !== this.textTarget) this.textTarget.value = note.text;
		for (const button of this.tagTargets) button.setAttribute("aria-pressed", String(note.tags.includes(button.dataset.tag)));
	}

	get note() {
		return this.id ? this.board?.state.view.peek().notes?.[this.id] : null;
	}

	focusField({ params: { field } }: { params: { field: Field } }) {
		this.sessions[field] = uuid();
	}

	typed({ params: { field } }: { params: { field: Field } }) {
		if (field === "mark") this.markTarget.value = clampMark(this.markTarget.value);
		clearTimeout(this.timers[field]);
		this.timers[field] = setTimeout(() => this.commit(field), WRITE_MS);
	}

	blurField({ params: { field } }: { params: { field: Field } }) {
		this.commit(field);
		this.sessions[field] = undefined;
		const note = this.note;
		if (note) this.fill(note);
	}

	flush() {
		this.commit("mark");
		this.commit("text");
	}

	// An empty mark is never sent, and its field goes back to the saved one on blur.
	commit(field: Field) {
		clearTimeout(this.timers[field]);
		const note = this.note;
		if (!note) return;
		const value = field === "mark" ? this.markTarget.value.trim() : this.textTarget.value;
		if (value === note[field] || (field === "mark" && !value)) return;
		const session = this.sessions[field];
		this.board.updateNote(note.id, { [field]: value }, session ? { merge: `note:${note.id}:${session}` } : {});
	}

	toggle({ params: { tag } }: { params: { tag: string } }) {
		const note = this.note;
		if (!note) return;
		const tags = NOTE_TAGS.filter((letter) => (letter === tag) !== note.tags.includes(letter)).join("");
		this.board.updateNote(note.id, { tags });
	}

	remove() {
		const id = this.id;
		if (!id) return;
		for (const field of ["mark", "text"] as const) clearTimeout(this.timers[field]);
		this.board.removeNote(id);
	}

	dismiss() {
		this.board?.selectNote(null);
	}

	// Right of the mark and the chips beside it, left of the mark when that would leave the board or cover an open panel, and
	// clamped; positions are the frame's, which keeps clear of the screen's notches.
	place(note: LiveNote) {
		const { renderer } = this.board;
		const at = renderer?.imageToClient({ x: note.x, y: note.y });
		if (!at || window.matchMedia(SHEET).matches) return;
		const frame = (this.panelTarget.offsetParent ?? this.element).getBoundingClientRect();
		const panel = this.panelTarget.getBoundingClientRect();
		const chips = note.tags.length ? note.tags.length * CHIP_STEP : 0;
		const x = at.x - frame.left;
		let limit = frame.width - GAP;
		for (const open of this.element.querySelectorAll('[data-map-inset="panel"]')) {
			const { left, width } = open.getBoundingClientRect();
			if (width) limit = Math.min(limit, left - frame.left - GAP);
		}
		let left = x + NOTE_RADIUS + HALO + chips + GAP;
		if (left + panel.width > limit) left = x - NOTE_RADIUS - HALO - GAP - panel.width;
		left = Math.min(Math.max(left, GAP), Math.max(frame.width - panel.width - GAP, GAP));
		const top = Math.min(Math.max(at.y - frame.top - NOTE_RADIUS * 2, GAP), Math.max(frame.height - panel.height - GAP, GAP));
		this.panelTarget.style.left = `${left}px`;
		this.panelTarget.style.top = `${top}px`;
	}
}
